import { Selection, EnterElement, ScaleBand, range, scaleLinear, axisLeft, format } from "d3";
import { D3OneToOneRenderable } from "../../../D3/D3OneToOneRenderable";
import { ModalityGraphGroupReactCallbacks } from "../../../../Types/ReactCallbacks";
import { HeatmapTraceConfigJSON, getHeatmapTraceId } from "../../../../Types/Trace";
import { ColorSpectrum, getSpectrumInterpolator } from "../../../../Types/ColorSpectrum";
import { alpha } from "@mui/system";
import { MobergFontFamily } from "../../../../../../../../Moberg";
import { getNiceTicks, getTickYCorrection } from "../../../D3/axisUtils";
import { isThresholdRow, thresholdRowLabel } from "../../../../Types/GoalRanges";
import { MODALITY_LABEL_FONT_SIZE, AXIS_TICK_FONT_SIZE } from "../../Constants";

type D3HeatmapTraceLegendConfig = {
    yScale: ScaleBand<any>
    trace: HeatmapTraceConfigJSON
    graphId: string
    labelLanes?: boolean
}

export class D3HeatmapTraceLegend extends D3OneToOneRenderable<SVGGElement, SVGGElement, D3HeatmapTraceLegendConfig, ModalityGraphGroupReactCallbacks> {
    private gap = 8
    private width = 16
    private textYOffset = -16
    private gradientScale = scaleLinear()
    private colorScaleAxisClassName = "d3-heatmap-color-scale-axis"
    private labelClassName = "d3-heatmap-legend-label"

    constructor(root: SVGGElement, config: D3HeatmapTraceLegendConfig, className: string, reactCallbacks: ModalityGraphGroupReactCallbacks) {
        super(root, config, className, reactCallbacks)
        this.updateDerivedState()
        this.render()
    }

    // A % in goal row says its goal and how long each of its blocks is ("HR (% in goal 60 to 110, 5 Min bins)").
    private getLabelText() {
        return isThresholdRow(this.config.trace) ? thresholdRowLabel(this.config.trace) : this.config.trace.name
    }

    // In a graph of goal rows the label sits in the lane above its band (see
    // D3ModalityHeatmapGraph); any other row's label sits on the band's top edge as before.
    private getLabelTransform() {
        const lane = this.config.yScale.step() * this.config.yScale.paddingInner()
        const y = this.config.labelLanes && lane > 0 ? -lane - 1 : -this.textYOffset - 20
        return `translate(${this.width + this.gap}, ${y})`
    }

    // A band too thin for its tick labels (a goal strip) shows the colour bar alone rather
    // than numbers printed over each other.
    private getTickValues() {
        return isThresholdRow(this.config.trace) && this.config.yScale.bandwidth() < 24 ? [] : getNiceTicks(this.gradientScale)
    }

    protected updateDerivedState(): void {
        this.gradientScale
            .domain([this.config.trace.lowerBound, this.config.trace.upperBound])
            .range([this.config.yScale.bandwidth() - 1, 0]) // 1 px for the line width of the tick mark
    }

    protected enter(newElements: Selection<EnterElement, D3HeatmapTraceLegendConfig, any, any>): Selection<SVGGElement, D3HeatmapTraceLegendConfig, SVGGElement, any> {
        const legend = newElements
            .append("g")
            .attr("class", this.className)
            .attr("transform", `translate(${-this.width - this.gap}, ${this.config.yScale(getHeatmapTraceId(this.config.trace))})`)
            .style("color", this.getTraceColor)

        // Setup the SVG
        const legendSVG = legend.append("svg")
            .attr("width", this.width)
            .attr("height", this.config.yScale.bandwidth())
                
        const legendDefs = legendSVG.append("defs")

        this.createGradient(legendDefs, this.config.trace.colorSpectrum)

        legendSVG
            .append("rect")
            .attr("fill", `url(#${this.config.trace.colorSpectrum})`)
            .attr("width", this.width)
            .attr("height", this.config.yScale.bandwidth()) 

        // Create the axis
        const colorScaleAxis = legend
            .append("g")
            .attr("class", this.colorScaleAxisClassName)

        const d3AxisLeft = colorScaleAxis.call(
            axisLeft(this.gradientScale)
                .tickValues(this.getTickValues())
                .tickFormat(format(".4~f"))) // Up to 4 decimal place precision, but less if not needed.
                .style("font-size", AXIS_TICK_FONT_SIZE)
                .style("font-family", MobergFontFamily.REGULAR)

        d3AxisLeft.selectAll(".tick text")
            .attr("y", (d, i, nodes) => getTickYCorrection(i, nodes))

        // Remove the black line
        colorScaleAxis.select(".domain").attr("stroke", "transparent")

        // Text label
        // A % in goal row's label is wide enough for its goal and bin size; only the label itself
        // takes clicks, so the empty part of the box never covers the heatmap.
        const goalRow = isThresholdRow(this.config.trace)
        const foreignObject = legend
            .append("foreignObject")
            .attr("width", goalRow ? 420 : 200)
            .attr("height", 20)
            .attr("transform", this.getLabelTransform())
            .style("pointer-events", goalRow ? "none" : "")

        foreignObject.append<HTMLDivElement>("xhtml:button")
            .style("pointer-events", goalRow ? "auto" : "")
            .style("white-space", goalRow ? "nowrap" : "")
            .style("border", 0)
            .style("background", "var(--background-color)")
            .style("width", "fit-content")
            .style("color", "var(--text-color)")
            .style("padding-left", "4px")
            .style("padding-right", "8px")
            .attr("class", this.labelClassName)
            .style("font-weight", "bold")
            .style("font-family", MobergFontFamily.REGULAR)
            .style("font-size", MODALITY_LABEL_FONT_SIZE)
            .text(this.getLabelText())
            .on("mousedown", this.onMouseDown)
			.on("contextmenu", this.onContextMenu)

        return legend
    }

    protected update(updatedElements: Selection<SVGGElement, D3HeatmapTraceLegendConfig, any, any>): Selection<SVGGElement, D3HeatmapTraceLegendConfig, SVGGElement, any> {
        const legend = updatedElements
            .attr("transform", `translate(${-this.width - this.gap}, ${this.config.yScale(getHeatmapTraceId(this.config.trace))})`)
            .style("color", this.getTraceColor)

        const legendSVG = legend.select("svg")
        const legendDefs = legendSVG.select("defs") as Selection<SVGDefsElement, any, any, any>

        this.createGradient(legendDefs, this.config.trace.colorSpectrum)
        
        legendSVG.attr("height", this.config.yScale.bandwidth())
            .select("rect")
            .attr("height", this.config.yScale.bandwidth())

        // The band can move or resize (window resize, rows added), so the label follows it.
        legend.select("foreignObject")
            .attr("transform", this.getLabelTransform())

        legend.select("." + this.labelClassName)
            .style("font-family", MobergFontFamily.REGULAR)
            .style("font-size", MODALITY_LABEL_FONT_SIZE)
            .attr("transform", `translate(${this.width + this.gap}, ${-this.textYOffset})`)
            .text(this.getLabelText())
        
        legendSVG
            .select("rect")
            .attr("fill", `url(#${this.config.trace.colorSpectrum})`)

        const colorScaleAxis = legend.select<SVGGElement>("." + this.colorScaleAxisClassName)

        const d3AxisLeft = colorScaleAxis.call(
            axisLeft(this.gradientScale)
                .tickValues(this.getTickValues())
                .tickFormat(format(".4~f"))) // Up to 4 decimal place precision, but less if not needed.
                .style("font-size", AXIS_TICK_FONT_SIZE)
                .style("font-family", MobergFontFamily.REGULAR)
            
        d3AxisLeft.selectAll(".tick text")
            .attr("y", (d, i, nodes) => getTickYCorrection(i, nodes))

        return legend
    }

    private createGradient(defs: Selection<SVGDefsElement, any, any, any>, colorSpectrum: ColorSpectrum) {
        const gradientId = this.config.trace.colorSpectrum
        let gradient = defs.select<SVGLinearGradientElement>(`#${gradientId}`)
    
        if (gradient.empty()) {
            defs.selectChildren().remove()
    
            // Create a new linear gradient
            gradient = defs
                .append("linearGradient")
                .attr("id", gradientId)
                .attr("x1", "0%")
                .attr("x2", "0%")
                .attr("y1", "100%")
                .attr("y2", "0%")
    
            // Append color stops to the 
            const interpolator = getSpectrumInterpolator(colorSpectrum)
            
            const colorStops = range(11).map(value => ({
                offset: `${Math.round(100 * value / 10)}%`, color: interpolator(value / 10)
            }))
    
            colorStops.forEach(color => {
                gradient
                    .append("stop")
                    .attr("offset", color.offset)
                    .attr("stop-color", color.color)
            })
        }
    
        return gradientId
    }

    private getTraceColor = (config: D3HeatmapTraceLegendConfig) => {
        if (config.trace.isVisible ?? true) {
            return "#000000"
        }
        return alpha("#000000", 0.2)
    }

    private onMouseDown = (event: React.MouseEvent<HTMLButtonElement>, config: D3HeatmapTraceLegendConfig) => {
        if (event.buttons === 1) {
            this.reactCallbacks.toggleTraceVisibility(config.trace.toggleTarget?.graphId ?? this.config.graphId, config.trace.toggleTarget?.traceId ?? config.trace.id)
        }
    }

    private onContextMenu = (event: React.MouseEvent<HTMLButtonElement>, config: D3HeatmapTraceLegendConfig) => {
        event.preventDefault()
        // A goal strip row's menu acts on the measurement and graph it belongs to.
        const target = config.trace.toggleTarget
        this.reactCallbacks.openTraceVisibilityContextMenu(event, target ? { ...config.trace, id: target.traceId } : config.trace, target?.graphId ?? this.config.graphId)
    }
}

